import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

// Pages
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Volontari from './pages/Volontari';
import VolontarioForm from './pages/VolontarioForm';
import VolontarioDettaglio from './pages/VolontarioDettaglio';
import Eventi from './pages/Eventi';
import CalendarioEventi from './pages/CalendarioEventi';
import CalendarioScadenzario from './pages/CalendarioScadenzario';
import LibriSociali from './pages/LibriSociali';
import ExportRUNTS from './pages/ExportRUNTS';
import EventoForm from './pages/EventoForm';
import EventoDettaglio from './pages/EventoDettaglio';
import Presenze from './pages/Presenze';
import CheckinRapido from './pages/CheckinRapido';
import Coperture from './pages/Coperture';
import Documenti from './pages/Documenti';
import Logs from './pages/Logs';
import ImpostazioniEnte from './pages/ImpostazioniEnte';
import Profilo from './pages/Profilo';
import RegistroVolontari from './pages/RegistroVolontari';
import OreVolontari from './pages/OreVolontari';
import DiarioAttivita from './pages/DiarioAttivita';
import Notifiche from './pages/Notifiche';
import Reports from './pages/Reports';
import Pricing from './pages/Pricing';
import VolontariNonOccasionali from './pages/VolontariNonOccasionali';
import VolontariOccasionali from './pages/VolontariOccasionali';
import KitAmministrativo from './pages/KitAmministrativo';
import RendicontazioneSociale from './pages/RendicontazioneSociale';
import RendicontazioneEconomica from './pages/RendicontazioneEconomica';
import StatisticheVolontariato from './pages/StatisticheVolontariato';
import AdminDashboard from './pages/AdminDashboard';
import ManualeUso from './pages/ManualeUso';
import TerminiUtilizzo from './pages/TerminiUtilizzo';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import UsoAI from './pages/UsoAI';
import OnboardingWizard from './pages/OnboardingWizard';
import Comunicazioni from './pages/Comunicazioni';
import VerifyEmail from './pages/VerifyEmail';
import ResetPassword from './pages/ResetPassword';
import CertificatiPersonalizzati from './pages/CertificatiPersonalizzati';
import StatisticheAvanzatePro from './pages/StatisticheAvanzatePro';
import ImportMassivoIbrido from './pages/ImportMassivoIbrido';
import BilanciMinisteriali from './pages/BilanciMinisteriali';
import Soci from './pages/Soci';
import QuoteAssociative from './pages/QuoteAssociative';
import RegistroSoci from './pages/RegistroSoci';
import KitSoci from './pages/KitSoci';
import FamiliariAssistiti from './pages/FamiliariAssistiti';
import MagazzinoInventario from './pages/MagazzinoInventario';
import MagazzinoAssegnazioni from './pages/MagazzinoAssegnazioni';
import Automezzi from './pages/Automezzi';
import ExportDati from './pages/ExportDati';
import MigrazioneVolontari from './pages/MigrazioneVolontari';
import Brochure from './pages/Brochure';
import Convenzioni from './pages/Convenzioni';
import OrganiSociali from './pages/OrganiSociali';
import Assemblee from './pages/Assemblee';
import ConsiglioDirettivo from './pages/ConsiglioDirettivo';
import CondizioniAbbonamento from './pages/CondizioniAbbonamento';

// Layout
import Layout from './components/Layout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });
    
    if (!response.ok) {
      const error = await response.json();
      // Create a proper error object with response data for Login page to check
      const err = new Error(error.detail || 'Errore durante il login');
      err.response = { data: error };
      throw err;
    }
    
    const userData = await response.json();
    setUser(userData);
    // Save token to localStorage for pages that use Bearer auth
    if (userData.token) {
      localStorage.setItem('token', userData.token);
    }
    toast.success('Accesso effettuato con successo');
    return userData;
  };

  const register = async (email, password, name, enteName) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, name, ente_name: enteName })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Errore durante la registrazione');
      }
      
      const result = await response.json();
      
      // If email verification is required, don't set user yet
      if (result.requires_verification) {
        return result; // Return result for Login page to handle
      }
      
      // If no verification required (legacy), set user directly
      setUser(result);
      toast.success('Registrazione completata con successo');
      return result;
    } catch (error) {
      toast.error(error.message);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('token'); // Clear token on logout
      localStorage.removeItem('onboarding_completed');
      localStorage.removeItem('onboarding_step');
      toast.success('Logout effettuato');
    }
  };

  const setUserFromCallback = (userData) => {
    setUser(userData);
    // Save token to localStorage for pages that use Bearer auth
    if (userData?.token) {
      localStorage.setItem('token', userData.token);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUserFromCallback, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route with Onboarding Check
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  useEffect(() => {
    const checkOnboarding = async () => {
      // Skip onboarding check for certain routes
      const skipRoutes = ['/onboarding', '/manuale', '/termini', '/privacy', '/cookie-policy', '/uso-intelligenza-artificiale', '/pricing', '/profilo'];
      if (skipRoutes.some(route => location.pathname.startsWith(route))) {
        setOnboardingChecked(true);
        return;
      }
      
      // Skip for platform_admin - they don't need onboarding
      if (user?.role === 'platform_admin') {
        setOnboardingChecked(true);
        return;
      }

      try {
        // Check localStorage first (set by onboarding completion to avoid race condition)
        if (localStorage.getItem('onboarding_completed') === 'true') {
          setNeedsOnboarding(false);
          setOnboardingChecked(true);
          return;
        }
        
        const token = localStorage.getItem('token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const response = await fetch(`${API_URL}/api/users/onboarding-status`, {
          headers,
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          if (data.onboarding_completed) {
            localStorage.setItem('onboarding_completed', 'true');
          }
          setNeedsOnboarding(!data.onboarding_completed);
        }
      } catch (error) {
        console.error('Onboarding check failed:', error);
      } finally {
        setOnboardingChecked(true);
      }
    };

    if (user) {
      checkOnboarding();
    }
  }, [user, location.pathname]);

  if (loading || (user && !onboardingChecked)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center">
          <div className="spinner w-8 h-8" />
          <p className="mt-4 text-slate-500">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to onboarding if not completed (except for allowed routes)
  if (needsOnboarding && !location.pathname.startsWith('/onboarding')) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/brochure" element={<Brochure />} />
      <Route path="/convenzioni" element={<Convenzioni />} />
      
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="volontari" element={<Volontari />} />
        <Route path="volontari/nuovo" element={<VolontarioForm />} />
        <Route path="volontari/:id" element={<VolontarioDettaglio />} />
        <Route path="volontari/:id/modifica" element={<VolontarioForm />} />
        <Route path="registro" element={<RegistroVolontari />} />
        <Route path="volontari-non-occasionali" element={<VolontariNonOccasionali />} />
        <Route path="volontari-occasionali" element={<VolontariOccasionali />} />
        <Route path="eventi" element={<Eventi />} />
        <Route path="eventi/calendario" element={<CalendarioEventi />} />
        <Route path="scadenzario" element={<CalendarioScadenzario />} />
        <Route path="eventi/nuovo" element={<EventoForm />} />
        <Route path="eventi/:id" element={<EventoDettaglio />} />
        <Route path="eventi/:id/modifica" element={<EventoForm />} />
        <Route path="presenze" element={<Presenze />} />
        <Route path="checkin" element={<CheckinRapido />} />
        <Route path="coperture" element={<Coperture />} />
        <Route path="documenti" element={<Documenti />} />
        <Route path="libri-sociali" element={<LibriSociali />} />
        <Route path="export-runts" element={<ExportRUNTS />} />
        <Route path="reports" element={<Reports />} />
        <Route path="logs" element={<Logs />} />
        <Route path="ore-volontari" element={<OreVolontari />} />
        <Route path="diario" element={<DiarioAttivita />} />
        <Route path="notifiche" element={<Notifiche />} />
        <Route path="impostazioni" element={<ImpostazioniEnte />} />
        <Route path="profilo" element={<Profilo />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="kit-amministrativo" element={<KitAmministrativo />} />
        <Route path="rendicontazione" element={<RendicontazioneSociale />} />
        <Route path="statistiche" element={<StatisticheVolontariato />} />
        <Route path="admin" element={<AdminDashboard />} />
        <Route path="manuale" element={<ManualeUso />} />
        <Route path="termini" element={<TerminiUtilizzo />} />
        <Route path="condizioni-abbonamento" element={<CondizioniAbbonamento />} />
        <Route path="privacy" element={<PrivacyPolicy />} />
        <Route path="cookie-policy" element={<CookiePolicy />} />
        <Route path="uso-intelligenza-artificiale" element={<UsoAI />} />
        <Route path="onboarding" element={<OnboardingWizard />} />
        <Route path="comunicazioni" element={<Comunicazioni />} />
        <Route path="contabilita" element={<RendicontazioneEconomica />} />
        <Route path="certificati" element={<CertificatiPersonalizzati />} />
        <Route path="statistiche-avanzate" element={<StatisticheAvanzatePro />} />
        <Route path="import" element={<ImportMassivoIbrido />} />
        <Route path="bilanci-ministeriali" element={<BilanciMinisteriali />} />
        <Route path="soci" element={<Soci />} />
        <Route path="soci/:id" element={<Soci />} />
        <Route path="quote-associative" element={<QuoteAssociative />} />
        <Route path="registro-soci" element={<RegistroSoci />} />
        <Route path="kit-soci" element={<KitSoci />} />
        <Route path="familiari-assistiti" element={<FamiliariAssistiti />} />
        <Route path="magazzino" element={<MagazzinoInventario />} />
        <Route path="magazzino/assegnazioni" element={<MagazzinoAssegnazioni />} />
        <Route path="automezzi" element={<Automezzi />} />
        <Route path="export-dati" element={<ExportDati />} />
        <Route path="migrazione-volontari" element={<MigrazioneVolontari />} />
        {/* Governance Routes */}
        <Route path="governance" element={<OrganiSociali />} />
        <Route path="assemblee" element={<Assemblee />} />
        <Route path="consiglio-direttivo" element={<ConsiglioDirettivo />} />
      </Route>
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
